import { useState, MouseEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  useTheme,
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle,
  Settings,
  Logout,
  Dashboard,
  FolderOpen,
} from '@mui/icons-material'

import { RootState, AppDispatch } from '../../store'
import { logout } from '../../store/slices/authSlice'
import NotificationCenter from '../common/NotificationCenter'
import { getInitials } from '../../utils/userColors'

interface HeaderProps {
  onSidebarToggle: () => void
}

const Header: React.FC<HeaderProps> = ({ onSidebarToggle }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const theme = useTheme()
  const { user } = useSelector((state: RootState) => state.auth)
  const { sidebarOpen } = useSelector((state: RootState) => state.ui)
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
    handleProfileMenuClose()
  }

  const handleProfileClick = () => {
    navigate('/profile')
    handleProfileMenuClose()
  }

  const menuItems = [
    {
      label: 'Profile',
      icon: <AccountCircle fontSize="small" />,
      onClick: handleProfileClick,
    },
    {
      label: 'Settings',
      icon: <Settings fontSize="small" />,
      onClick: () => {
        // TODO: Implement settings page
        handleProfileMenuClose()
      },
    },
    { divider: true },
    {
      label: 'Logout',
      icon: <Logout fontSize="small" />,
      onClick: handleLogout,
    },
  ]

  return (
      <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#ffffff',
        color: 'text.primary',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        // Make header responsive to sidebar state
        width: {
          xs: '100%', // Full width on mobile
          md: sidebarOpen ? `calc(100% - 280px)` : '100%', // Adjust width based on sidebar
        },
        left: {
          xs: 0, // Start from left on mobile
          md: sidebarOpen ? '280px' : 0, // Adjust left position based on sidebar
        },
        transition: theme.transitions.create(['width', 'left'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          edge="start"
          onClick={onSidebarToggle}
          sx={{ 
            mr: 2,
            color: '#64748b',
            '&:hover': {
              backgroundColor: '#f1f5f9',
              color: '#1e293b',
            }
          }}
        >
          <MenuIcon />
        </IconButton>

        <Typography
          variant="h6"
          component="div"
          sx={{ 
            flexGrow: 1,
            fontWeight: 700,
            fontSize: '1.125rem',
            color: '#1e293b',
          }}
        >
          SAR Vexa
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Button
            startIcon={<Dashboard />}
            onClick={() => navigate('/dashboard')}
            sx={{ 
              display: { xs: 'none', sm: 'flex' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: location.pathname === '/dashboard' ? '#3b82f6' : '#64748b',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                color: '#3b82f6',
              }
            }}
          >
            Dashboard
          </Button>
          
          <Button
            startIcon={<FolderOpen />}
            onClick={() => navigate('/projects')}
            sx={{ 
              display: { xs: 'none', sm: 'flex' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: location.pathname === '/projects' ? '#3b82f6' : '#64748b',
              '&:hover': {
                backgroundColor: '#f1f5f9',
                color: '#3b82f6',
              }
            }}
          >
            Projects
          </Button>

          <NotificationCenter />

          <IconButton
            onClick={handleProfileMenuOpen}
            size="small"
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{
              '&:hover': {
                backgroundColor: '#f1f5f9',
              }
            }}
          >
            <Avatar
              src={user?.avatar}
              alt={user?.name}
              sx={{ 
                width: 36, 
                height: 36,
                border: open ? '2px solid #3b82f6' : '2px solid transparent',
                boxShadow: open ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : 'none',
                transition: 'all 0.2s ease',
              }}
            >
              {getInitials(user?.name)}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleProfileMenuClose}
          onClick={handleProfileMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              mt: 1.5,
              minWidth: 240,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              backgroundColor: '#ffffff',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 20,
                width: 10,
                height: 10,
                bgcolor: '#ffffff',
                borderLeft: '1px solid #e2e8f0',
                borderTop: '1px solid #e2e8f0',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User Info Section */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Avatar 
                src={user?.avatar} 
                sx={{ 
                  width: 40, 
                  height: 40,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                {getInitials(user?.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    color: '#1e293b',
                    lineHeight: 1.3,
                  }}
                >
                  {user?.name}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    color: '#64748b',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.email}
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Menu Items */}
          <Box sx={{ py: 0.5 }}>
            {menuItems.map((item, index) => {
              if ('divider' in item) {
                return (
                  <Divider 
                    key={index} 
                    sx={{ 
                      my: 0.5,
                      borderColor: '#e2e8f0',
                    }} 
                  />
                )
              }
              
              const isLogout = item.label === 'Logout'
              
              return (
                <MenuItem 
                  key={index} 
                  onClick={item.onClick}
                  sx={{
                    py: 0.875,
                    px: 1.5,
                    mx: 0.5,
                    my: 0.25,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isLogout ? '#fee2e2' : '#f1f5f9',
                      '& .MuiListItemIcon-root': {
                        color: isLogout ? '#ef4444' : '#3b82f6',
                      },
                      '& .MuiListItemText-primary': {
                        color: isLogout ? '#ef4444' : '#1e293b',
                      }
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: '#64748b',
                      minWidth: 36,
                      '& svg': {
                        fontSize: '1.1rem',
                      }
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: {
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        color: '#1e293b',
                      }
                    }}
                  />
                </MenuItem>
              )
            })}
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Header
